/**
 * Governance Actions Hooks (DC-1097)
 *
 * React hooks for governance action API integration.
 * Provides data fetching and mutation functions for:
 * - Role assignment
 * - RACI matrix creation
 * - Policy template adoption
 *
 * @version 1.0.0
 * @date 2026-01-26
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import type {
  GovernanceAction,
  GovernanceActionsListResponse,
  GovernanceActionResponse,
  RACITemplate,
  PolicyTemplate,
  TemplatesListResponse,
  TemplateResponse,
  RoleAssignmentInput,
  RACICreationInput,
  PolicyAdoptionInput,
  Employee,
  Persona,
} from './types';

// ============================================================================
// API ENDPOINTS
// ============================================================================

const API_BASE = '/api/governance';

// ============================================================================
// AUTH HEADERS HELPER
// ============================================================================

/**
 * Read auth headers from localStorage for non-hook API calls.
 * Mirrors the headers that useApiClient adds automatically.
 */
function getAuthHeaders(): Record<string, string> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  const token = localStorage.getItem('compass_access_token');
  const userJson = localStorage.getItem('compass_user');

  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  if (userJson) {
    try {
      const user = JSON.parse(userJson);
      if (user.id) {
        headers['X-User-Id'] = user.id;
      }
      if (user.organization?.id) {
        headers['X-Org-Id'] = user.organization.id;
      }
    } catch {
      // ignore parse errors
    }
  }

  return headers;
}

// ============================================================================
// GOVERNANCE ACTIONS HOOK
// ============================================================================

/**
 * Hook for fetching governance actions for a journey
 */
export function useGovernanceActions(journeyId: string) {
  const [actions, setActions] = useState<GovernanceAction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchActions = useCallback(async () => {
    if (!journeyId) {
      setActions([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`${API_BASE}/actions/${journeyId}`);
      const data: GovernanceActionsListResponse = await response.json();

      if (data.success) {
        setActions(data.actions);
      } else {
        setError(data.error || 'Failed to fetch actions');
      }
    } catch (err: unknown) {
      console.error('[useGovernanceActions] Fetch error:', err);
      setError(err instanceof Error ? err.message : 'Network error');
    } finally {
      setLoading(false);
    }
  }, [journeyId]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  return { actions, loading, error, refetch: fetchActions };
}

// ============================================================================
// COMPLETED ACTIONS HOOK
// ============================================================================

/**
 * Hook for fetching completed governance actions for a journey.
 * Returns a lookup map keyed by `action_type:persona_id` or `action_type:recommendation_id`
 * for quick checking of completion state on governance buttons.
 */
export function useCompletedActions(journeyId: string) {
  const { actions, loading, error, refetch } = useGovernanceActions(journeyId);

  // Build lookup map: "action_type|persona_id|recommendation_id" -> GovernanceAction
  const completedMap = useMemo(() => {
    const map = new Map<string, GovernanceAction>();
    for (const action of actions) {
      if (action.status === 'completed') {
        // Key for assign_role: type + persona_id (from action_data)
        const data = action.action_data as Record<string, unknown>;
        if (action.action_type === 'assign_role' && data.persona_id) {
          const key = `assign_role|${data.persona_id}|${action.recommendation_id || ''}`;
          map.set(key, action);
        }
        // Key for create_raci: type + recommendation_id
        if (action.action_type === 'create_raci') {
          const key = `create_raci|${action.recommendation_id || ''}`;
          map.set(key, action);
        }
        // Key for use_template: type + recommendation_id
        if (action.action_type === 'use_template') {
          const key = `use_template|${action.recommendation_id || ''}`;
          map.set(key, action);
        }
        // Key for review_policy: type + recommendation_id
        if (action.action_type === 'review_policy') {
          const key = `review_policy|${action.recommendation_id || ''}`;
          map.set(key, action);
        }
      }
    }
    return map;
  }, [actions]);

  /**
   * Check if a specific governance step has been completed.
   * Returns the completed action if found, null otherwise.
   */
  const isStepCompleted = useCallback(
    (actionType: string, recommendationId?: string, personaId?: string): GovernanceAction | null => {
      if (actionType === 'assign_role' && personaId) {
        return completedMap.get(`assign_role|${personaId}|${recommendationId || ''}`) || null;
      }
      return completedMap.get(`${actionType}|${recommendationId || ''}`) || null;
    },
    [completedMap]
  );

  return { actions, completedMap, isStepCompleted, loading, error, refetch };
}

// ============================================================================
// RACI TEMPLATES HOOK
// ============================================================================

/**
 * Hook for fetching RACI templates
 */
export function useRACITemplates() {
  const [templates, setTemplates] = useState<RACITemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/templates/raci`);
        const data: TemplatesListResponse<RACITemplate> = await response.json();

        if (data.success) {
          setTemplates(data.templates);
        } else {
          setError(data.error || 'Failed to fetch RACI templates');
        }
      } catch (err: unknown) {
        console.error('[useRACITemplates] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    }

    fetchTemplates();
  }, []);

  return { templates, loading, error };
}

/**
 * Hook for fetching a single RACI template
 */
export function useRACITemplate(templateId: string | undefined) {
  const [template, setTemplate] = useState<RACITemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTemplate() {
      if (!templateId) {
        setTemplate(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/templates/raci/${templateId}`);
        const data: TemplateResponse<RACITemplate> = await response.json();

        if (data.success && data.template) {
          setTemplate(data.template);
        } else {
          setError(data.error || 'Failed to fetch RACI template');
        }
      } catch (err: unknown) {
        console.error('[useRACITemplate] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    }

    fetchTemplate();
  }, [templateId]);

  return { template, loading, error };
}

// ============================================================================
// POLICY TEMPLATES HOOK
// ============================================================================

/**
 * Hook for fetching policy templates
 */
export function usePolicyTemplates() {
  const [templates, setTemplates] = useState<PolicyTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTemplates() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/templates/policies`);
        const data: TemplatesListResponse<PolicyTemplate> = await response.json();

        if (data.success) {
          setTemplates(data.templates);
        } else {
          setError(data.error || 'Failed to fetch policy templates');
        }
      } catch (err: unknown) {
        console.error('[usePolicyTemplates] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    }

    fetchTemplates();
  }, []);

  return { templates, loading, error };
}

/**
 * Hook for fetching a single policy template
 */
export function usePolicyTemplate(templateId: string | undefined) {
  const [template, setTemplate] = useState<PolicyTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchTemplate() {
      if (!templateId) {
        setTemplate(null);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/templates/policies/${templateId}`);
        const data: TemplateResponse<PolicyTemplate> = await response.json();

        if (data.success && data.template) {
          setTemplate(data.template);
        } else {
          setError(data.error || 'Failed to fetch policy template');
        }
      } catch (err: unknown) {
        console.error('[usePolicyTemplate] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    }

    fetchTemplate();
  }, [templateId]);

  return { template, loading, error };
}

// ============================================================================
// PERSONAS AND EMPLOYEES HOOKS
// ============================================================================

/**
 * Hook for fetching available personas
 */
export function usePersonas() {
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchPersonas() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch(`${API_BASE}/personas`);
        const data = await response.json();

        if (data.success) {
          setPersonas(data.personas);
        } else {
          setError(data.error || 'Failed to fetch personas');
        }
      } catch (err: unknown) {
        console.error('[usePersonas] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    }

    fetchPersonas();
  }, []);

  return { personas, loading, error };
}

/**
 * Hook for fetching employees
 */
export function useEmployees() {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    async function fetchEmployees() {
      try {
        setLoading(true);
        setError(null);

        const response = await fetch('/api/employees');
        const data = await response.json();

        if (data.success) {
          // Map server fields (display_name, job_title) to client Employee type (name, title)
          interface ServerEmployee {
            employee_id: string;
            display_name?: string;
            name?: string;
            email: string;
            department?: string;
            job_title?: string;
            title?: string;
          }
          const mapped = (data.employees || []).map((e: ServerEmployee) => ({
            employee_id: e.employee_id,
            name: e.display_name || e.name || e.email,
            email: e.email,
            department: e.department,
            title: e.job_title || e.title,
          }));
          setEmployees(mapped);
        } else {
          setError(data.error || 'Failed to fetch employees');
        }
      } catch (err: unknown) {
        console.error('[useEmployees] Fetch error:', err);
        setError(err instanceof Error ? err.message : 'Network error');
      } finally {
        setLoading(false);
      }
    }

    fetchEmployees();
  }, []);

  return { employees, loading, error };
}

// ============================================================================
// ACTION MUTATION FUNCTIONS
// ============================================================================

/**
 * Assign a role to an employee
 */
export async function assignRole(input: RoleAssignmentInput): Promise<GovernanceAction> {
  const response = await fetch(`${API_BASE}/actions/assign-role`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  const data: GovernanceActionResponse = await response.json();

  if (!data.success || !data.action) {
    throw new Error(data.error || data.message || 'Failed to assign role');
  }

  return data.action;
}

/**
 * Create a RACI matrix from a template
 */
export async function createRACIMatrix(input: RACICreationInput): Promise<GovernanceAction> {
  const response = await fetch(`${API_BASE}/actions/create-raci`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  const data: GovernanceActionResponse = await response.json();

  if (!data.success || !data.action) {
    throw new Error(data.error || data.message || 'Failed to create RACI matrix');
  }

  return data.action;
}

/**
 * Adopt a policy template with customizations
 */
export async function adoptPolicy(input: PolicyAdoptionInput): Promise<GovernanceAction> {
  const response = await fetch(`${API_BASE}/actions/adopt-policy`, {
    method: 'POST',
    headers: getAuthHeaders(),
    body: JSON.stringify(input),
  });

  const data: GovernanceActionResponse = await response.json();

  if (!data.success || !data.action) {
    throw new Error(data.error || data.message || 'Failed to adopt policy');
  }

  return data.action;
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Get RACI role display name
 */
export function getRACIRoleLabel(role: 'R' | 'A' | 'C' | 'I'): string {
  switch (role) {
    case 'R': return 'Responsible';
    case 'A': return 'Accountable';
    case 'C': return 'Consulted';
    case 'I': return 'Informed';
    default: return role;
  }
}

/**
 * Get RACI role description
 */
export function getRACIRoleDescription(role: 'R' | 'A' | 'C' | 'I'): string {
  switch (role) {
    case 'R': return 'Person who does the work';
    case 'A': return 'Decision maker (only one per activity)';
    case 'C': return 'Provides input before action';
    case 'I': return 'Notified after action';
    default: return '';
  }
}

/**
 * Validate RACI assignments have required roles
 */
export function validateRACIAssignments(
  assignments: Array<{ role: 'R' | 'A' | 'C' | 'I'; employee_id?: string }>
): { valid: boolean; errors: string[] } {
  const errors: string[] = [];

  const hasResponsible = assignments.some(a => a.role === 'R');
  const hasAccountable = assignments.some(a => a.role === 'A');
  const accountableCount = assignments.filter(a => a.role === 'A').length;

  if (!hasResponsible) {
    errors.push('At least one Responsible (R) role is required');
  }

  if (!hasAccountable) {
    errors.push('At least one Accountable (A) role is required');
  }

  if (accountableCount > 1) {
    errors.push('Only one Accountable (A) role is allowed per activity');
  }

  return { valid: errors.length === 0, errors };
}
